import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getApiUrl } from '../config';
import { useNotification } from './Notification';
import { useResponsive } from '../hooks/useResponsive';
import { useFriends } from '../hooks/useFriends';
import { AppHeader } from './AppHeader';
import Avatar from './ui/Avatar';
import { FriendActionsModal } from './FriendActionsModal';
import { theme, alpha } from '../theme';
import type { Friend } from '../types';

interface User {
  id: string;
  name: string;
  nickname?: string;
  avatar?: string;
  position?: string;
  rating: number;
  preferredPosition?: string;
  preferredFoot?: string;
}

type FriendsTab = 'all' | 'received' | 'sent';

const FriendsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [searchPage, setSearchPage] = useState(1);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchTotalPages, setSearchTotalPages] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeTab, setActiveTab] = useState<FriendsTab>('all');
  const [selectedFriend, setSelectedFriend] = useState<Friend | null>(null);
  const { showNotification } = useNotification();

  const SEARCH_PAGE_SIZE = 20;
  const {
    friends,
    requests,
    sentRequests,
    loading: loadingFriends,
    acceptRequest,
    rejectRequest,
    cancelRequest,
    removeFriend,
    acceptingId,
    rejectingId,
    cancellingId,
    removingId,
  } = useFriends({
    onError: (msg) => showNotification(msg, 'error'),
    onAccept: () => showNotification('Solicitud aceptada', 'success'),
    onReject: () => showNotification('Solicitud rechazada', 'success'),
    onCancel: () => showNotification('Solicitud cancelada', 'info'),
    onRemove: () => showNotification('Amigo eliminado', 'info'),
  });

  const fetchSearchPage = async (page: number, append: boolean) => {
    const url = `${getApiUrl('/api/users/search')}?q=${encodeURIComponent(searchQuery.trim())}&page=${page}&limit=${SEARCH_PAGE_SIZE}`;
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) {
      const errorData = await response.json();
      showNotification(errorData.error || 'Error al buscar usuarios', 'error');
      if (!append) setSearchResults([]);
      return;
    }
    const data = await response.json();
    const users = data.users || [];
    const total = data.total ?? 0;
    const totalPages = Math.max(1, data.totalPages ?? 1);
    setSearchTotal(total);
    setSearchTotalPages(totalPages);
    if (append) setSearchResults(prev => [...prev, ...users]);
    else setSearchResults(users);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      showNotification('Por favor ingresa un nombre para buscar', 'warning');
      return;
    }
    setIsSearching(true);
    setHasSearched(true);
    setSearchPage(1);
    try {
      await fetchSearchPage(1, false);
    } catch {
      showNotification('Error de conexión', 'error');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleLoadMore = async () => {
    if (loadingMore || searchPage >= searchTotalPages) return;
    setLoadingMore(true);
    const nextPage = searchPage + 1;
    try {
      await fetchSearchPage(nextPage, true);
      setSearchPage(nextPage);
    } catch {
      showNotification('Error de conexión', 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleUserClick = (userId: string) => {
    navigate(`/public-profile/${userId}`);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getPositionColor = (position: string) => {
    const colors: { [key: string]: string } = {
      'POR': '#FF6B6B',
      'DEF': '#4ECDC4',
      'MED': '#45B7D1',
      'DEL': '#96CEB4',
    };
    return colors[position] || theme.colors.primary;
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 4.5) return '#FFD700';
    if (rating >= 4.0) return '#C0C0C0';
    if (rating >= 3.5) return '#CD7F32';
    return theme.colors.textSecondary;
  };

  const handleFriendCardClick = (friend: Friend) => setSelectedFriend(friend);

  const handleRemoveFriend = async (friendId: string) => {
    const ok = await removeFriend(friendId);
    if (ok) setSelectedFriend(null);
  };

  const tabs: { id: FriendsTab; label: string; count: number }[] = [
    { id: 'all', label: 'Amigos', count: friends.length },
    { id: 'received', label: 'Solicitudes', count: requests.length },
    { id: 'sent', label: 'Enviadas', count: sentRequests.length },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.colors.background,
      padding: isMobile ? '80px 16px 20px' : '20px 40px'
    }}>
      <AppHeader title="Amigos" showBackButton />

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '20px',
          overflowX: 'auto',
          paddingBottom: '4px',
        }}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 16px',
                background: isActive ? theme.colors.primary : theme.colors.surface,
                color: isActive ? theme.colors.background : theme.colors.text,
                border: `1px solid ${isActive ? theme.colors.primary : theme.colors.border}`,
                borderRadius: '999px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {tab.label}
              {tab.count > 0 && (
                <span
                  style={{
                    background: isActive ? theme.colors.background : theme.colors.primary,
                    color: isActive ? theme.colors.primary : theme.colors.background,
                    borderRadius: '999px',
                    padding: '1px 8px',
                    fontSize: '12px',
                    fontWeight: 700,
                  }}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div style={{
        background: theme.colors.surface,
        borderRadius: '20px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        border: `1px solid ${theme.colors.border}`
      }}>
        {activeTab === 'all' && (
          <>
            <h2 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: theme.colors.text,
              marginBottom: '16px',
              fontFamily: theme.fonts.heading
            }}>
              👥 Mis amigos {!loadingFriends && `(${friends.length})`}
            </h2>
            {loadingFriends ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid transparent',
                  borderTop: `3px solid ${theme.colors.primary}`,
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              </div>
            ) : friends.length === 0 ? (
              <p style={{ color: theme.colors.textSecondary, fontSize: '14px', margin: 0 }}>
                Aún no tenés amigos. Buscá usuarios más abajo y enviáles solicitud desde su perfil.
              </p>
            ) : (
              <div style={{
                display: 'grid',
                gap: '12px',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))'
              }}>
                {friends.map((friend: Friend) => (
                  <button
                    type="button"
                    key={friend.id}
                    onClick={() => handleFriendCardClick(friend)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      padding: '14px 16px',
                      background: theme.colors.background,
                      borderRadius: '12px',
                      border: `1px solid ${theme.colors.border}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'left',
                      width: '100%',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = theme.colors.primary;
                      e.currentTarget.style.boxShadow = `0 4px 12px rgba(0,0,0,0.1)`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = theme.colors.border;
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: friend.avatar ? 'transparent' : theme.colors.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      flexShrink: 0
                    }}>
                      <Avatar src={friend.avatar} name={friend.name} size={48} bg="transparent" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: theme.colors.text, fontSize: '15px' }}>{friend.name}</div>
                      {friend.nickname && (
                        <div style={{ fontSize: '13px', color: theme.colors.textSecondary }}>"{friend.nickname}"</div>
                      )}
                      <div style={{ fontSize: '13px', color: theme.colors.textSecondary }}>⭐ {friend.rating.toFixed(1)}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'received' && (
          <>
            <h2 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: theme.colors.text,
              marginBottom: '16px',
              fontFamily: theme.fonts.heading,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🤝 Solicitudes recibidas
            </h2>
            {requests.length === 0 ? (
              <p style={{ color: theme.colors.textSecondary, fontSize: '14px', margin: 0 }}>
                No tenés solicitudes pendientes.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {requests.map((req) => (
                  <div
                    key={req.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '14px 16px',
                      background: theme.colors.background,
                      borderRadius: '12px',
                      border: `1px solid ${theme.colors.border}`
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => navigate(`/public-profile/${req.senderId}`)}
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: req.senderAvatar ? 'transparent' : theme.colors.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        flexShrink: 0,
                        border: 'none',
                        padding: 0
                      }}
                    >
                      {req.senderAvatar ? (
                        <img src={req.senderAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span style={{ fontSize: '20px' }}>👤</span>
                      )}
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: theme.colors.text, fontSize: '15px' }}>{req.senderName}</div>
                      {req.senderRating != null && (
                        <div style={{ fontSize: '13px', color: theme.colors.textSecondary }}>
                          ⭐ {req.senderRating.toFixed(1)}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                      <button
                        type="button"
                        disabled={rejectingId === req.id || acceptingId === req.id}
                        onClick={() => rejectRequest(req.id)}
                        style={{
                          padding: '8px 14px',
                          background: 'transparent',
                          color: theme.colors.textSecondary,
                          border: `1px solid ${theme.colors.border}`,
                          borderRadius: '10px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: rejectingId === req.id || acceptingId === req.id ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {rejectingId === req.id ? '...' : 'Rechazar'}
                      </button>
                      <button
                        type="button"
                        disabled={acceptingId === req.id || rejectingId === req.id}
                        onClick={() => acceptRequest(req.id)}
                        style={{
                          padding: '8px 14px',
                          background: theme.colors.primary,
                          color: theme.colors.background,
                          border: 'none',
                          borderRadius: '10px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: acceptingId === req.id || rejectingId === req.id ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {acceptingId === req.id ? '...' : 'Aceptar'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'sent' && (
          <>
            <h2 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: theme.colors.text,
              marginBottom: '16px',
              fontFamily: theme.fonts.heading,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              📤 Solicitudes enviadas
            </h2>
            {sentRequests.length === 0 ? (
              <p style={{ color: theme.colors.textSecondary, fontSize: '14px', margin: 0 }}>
                No tenés solicitudes pendientes de respuesta.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {sentRequests.map((sent) => (
                  <div
                    key={sent.requestId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '14px 16px',
                      background: theme.colors.background,
                      borderRadius: '12px',
                      border: `1px solid ${theme.colors.border}`
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => navigate(`/public-profile/${sent.receiver.id}`)}
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: sent.receiver.avatar ? 'transparent' : theme.colors.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        flexShrink: 0,
                        border: 'none',
                        padding: 0
                      }}
                    >
                      <Avatar src={sent.receiver.avatar} name={sent.receiver.name} size={48} bg="transparent" />
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: theme.colors.text, fontSize: '15px' }}>{sent.receiver.name}</div>
                      <div style={{ fontSize: '13px', color: theme.colors.textSecondary }}>
                        ⭐ {sent.receiver.rating.toFixed(1)} · Pendiente
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={cancellingId === sent.requestId}
                      onClick={() => cancelRequest(sent.requestId)}
                      style={{
                        padding: '8px 14px',
                        background: 'transparent',
                        color: '#EF4444',
                        border: `1px solid #EF4444`,
                        borderRadius: '10px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: cancellingId === sent.requestId ? 'not-allowed' : 'pointer',
                        opacity: cancellingId === sent.requestId ? 0.6 : 1,
                        flexShrink: 0,
                      }}
                    >
                      {cancellingId === sent.requestId ? '...' : 'Cancelar'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Search Section */}
      <div style={{
        background: theme.colors.surface,
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '32px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        border: `1px solid ${theme.colors.border}`
      }}>
        <h1 style={{
          fontSize: isMobile ? '28px' : '36px',
          fontWeight: 'bold',
          color: theme.colors.text,
          marginBottom: '8px',
          textAlign: 'center',
          fontFamily: theme.fonts.heading
        }}>
          🔍 Encuentra a tus Amigos
        </h1>
        <p style={{
          fontSize: '16px',
          color: theme.colors.textSecondary,
          textAlign: 'center',
          marginBottom: '32px',
          lineHeight: 1.6
        }}>
          Busca por nombre y descubre jugadores increíbles en tu zona
        </p>

        {/* Search Input */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '24px'
        }}>
          <div style={{
            flex: 1,
            position: 'relative'
          }}>
            <span
              style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1,
                fontSize: '20px',
                color: theme.colors.textSecondary
              }}
            >
              🔍
            </span>
            <input
              type="text"
              placeholder="Buscar por nombre..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              style={{
                width: '100%',
                padding: '16px 16px 16px 48px',
                fontSize: '16px',
                border: `2px solid ${theme.colors.border}`,
                borderRadius: '12px',
                background: theme.colors.background,
                color: theme.colors.text,
                fontFamily: theme.fonts.body,
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = theme.colors.primary;
                e.target.style.boxShadow = `0 0 0 3px ${alpha('primary', '20')}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = theme.colors.border;
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={isSearching}
            style={{
              padding: '16px 24px',
              background: isSearching ? theme.colors.textSecondary : theme.colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: isSearching ? 'not-allowed' : 'pointer',
              fontFamily: theme.fonts.heading,
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: '120px',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              if (!isSearching) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,230,118,0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSearching) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            {isSearching ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid transparent',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Buscando...
              </>
            ) : (
              <>
                <span style={{ fontSize: '16px' }}>🔍</span>
                Buscar
              </>
            )}
          </button>
        </div>

        {/* Search Tips */}
        <div style={{
          background: `${alpha('primary', '10')}`,
          border: `1px solid ${alpha('primary', '30')}`,
          borderRadius: '12px',
          padding: '16px',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '14px',
            color: theme.colors.primary,
            margin: 0,
            fontFamily: theme.fonts.body
          }}>
            💡 <strong>Tip:</strong> Puedes buscar por nombre completo, apodo o parte del nombre
          </p>
        </div>
      </div>

      {/* Results Section */}
      {hasSearched && (
        <div style={{
          background: theme.colors.surface,
          borderRadius: '20px',
          padding: '24px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          border: `1px solid ${theme.colors.border}`
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: theme.colors.text,
            marginBottom: '24px',
            fontFamily: theme.fonts.heading
          }}>
            {isSearching ? 'Buscando...' : `Resultados (${searchResults.length}${searchTotal > 0 ? ` de ${searchTotal}` : ''})`}
          </h2>

          {isSearching ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '40px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid transparent',
                borderTop: `3px solid ${theme.colors.primary}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            </div>
          ) : searchResults.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: theme.colors.textSecondary
            }}>
              <span style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>👤</span>
              <p style={{ fontSize: '18px', marginBottom: '8px' }}>
                No se encontraron usuarios
              </p>
              <p style={{ fontSize: '14px' }}>
                Intenta con otro nombre o verifica la ortografía
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gap: '16px',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))'
            }}>
              {searchResults.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => handleUserClick(user.id)}
                  style={{
                    background: theme.colors.background,
                    borderRadius: '16px',
                    padding: '20px',
                    cursor: 'pointer',
                    border: `1px solid ${theme.colors.border}`,
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    textAlign: 'left',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.15)';
                    e.currentTarget.style.borderColor = theme.colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = theme.colors.border;
                  }}
                >
                  {/* User Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      background: user.avatar ? 'transparent' : theme.colors.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      border: `3px solid ${theme.colors.border}`
                    }}>
                      <Avatar src={user.avatar} name={user.name} size={56} bg="transparent" />
                    </div>

                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: theme.colors.text,
                        margin: '0 0 4px 0',
                        fontFamily: theme.fonts.heading
                      }}>
                        {user.name}
                      </h3>
                      {user.nickname && (
                        <p style={{
                          fontSize: '14px',
                          color: theme.colors.textSecondary,
                          margin: 0,
                          fontStyle: 'italic'
                        }}>
                          "{user.nickname}"
                        </p>
                      )}
                    </div>

                    {/* Rating */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '8px 12px',
                      background: `${getRatingColor(user.rating)}20`,
                      borderRadius: '20px',
                      border: `1px solid ${getRatingColor(user.rating)}40`
                    }}>
                      <span style={{ fontSize: '14px', color: getRatingColor(user.rating) }}>⭐</span>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: getRatingColor(user.rating)
                      }}>
                        {user.rating.toFixed(1)}
                      </span>
                    </div>
                  </div>

                  {/* User Details */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px'
                  }}>
                    {user.position && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        background: `${getPositionColor(user.position)}20`,
                        borderRadius: '8px',
                        border: `1px solid ${getPositionColor(user.position)}40`
                      }}>
                        <span style={{ fontSize: '12px', color: getPositionColor(user.position) }}>⚽</span>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: getPositionColor(user.position)
                        }}>
                          {user.position}
                        </span>
                      </div>
                    )}

                    {(user.preferredPosition && (
                      (user.preferredPosition || '').trim().toLowerCase() !== (user.position || '').trim().toLowerCase()
                    )) && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        background: `${alpha('primary', '20')}`,
                        borderRadius: '8px',
                        border: `1px solid ${alpha('primary', '40')}`
                      }}>
                        <span style={{ fontSize: '12px', color: theme.colors.primary }}>📍</span>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: theme.colors.primary
                        }}>
                          {user.preferredPosition}
                        </span>
                      </div>
                    )}

                    {user.preferredFoot && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        background: `${alpha('secondary', '20')}`,
                        borderRadius: '8px',
                        border: `1px solid ${alpha('secondary', '40')}`
                      }}>
                        <span style={{ fontSize: '12px', color: theme.colors.secondary }}>⚽</span>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: theme.colors.secondary
                        }}>
                          {user.preferredFoot}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Click Indicator */}
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: theme.colors.primary,
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    opacity: 0,
                    transition: 'opacity 0.3s ease'
                  }}>
                    VER PERFIL
                  </div>
                </button>
              ))}
            </div>
          )}

          {hasSearched && !isSearching && searchResults.length > 0 && searchPage < searchTotalPages && (
            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={loadingMore}
                style={{
                  padding: '12px 24px',
                  background: loadingMore ? theme.colors.textSecondary : theme.colors.primary,
                  color: theme.colors.background,
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: loadingMore ? 'not-allowed' : 'pointer'
                }}
              >
                {loadingMore ? 'Cargando...' : `Cargar más (${searchResults.length} de ${searchTotal})`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Friend actions modal */}
      <FriendActionsModal
        isOpen={!!selectedFriend}
        onClose={() => setSelectedFriend(null)}
        friend={selectedFriend}
        onViewProfile={(id) => navigate(`/public-profile/${id}`)}
        onRemoveFriend={handleRemoveFriend}
        removing={removingId === selectedFriend?.id}
      />

      {/* CSS for animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

    </div>
  );
};

export default FriendsPage;
